import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import type { NotificationPreferences } from '../types';
import i18n, { SupportedLanguage, supportedLanguages } from '../i18n';

// Storage key
const PREFERENCES_STORAGE_KEY = 'brickmod-preferences';

// Default notification preferences
const defaultNotificationPreferences: NotificationPreferences = {
  email: true,
  push: false,
  waitlistUpdates: true,
  dueDateReminders: true,
};

// State interface
interface PreferencesState {
  language: SupportedLanguage;
  notifications: NotificationPreferences;
}

// Action types
type PreferencesAction =
  | { type: 'SET_LANGUAGE'; payload: SupportedLanguage }
  | { type: 'UPDATE_NOTIFICATIONS'; payload: Partial<NotificationPreferences> }
  | { type: 'HYDRATE'; payload: PreferencesState };

// Get initial language from i18n or default to 'en'
function getInitialLanguage(): SupportedLanguage {
  const currentLang = i18n.language?.split('-')[0] as SupportedLanguage;
  if (supportedLanguages.includes(currentLang)) {
    return currentLang;
  }
  return 'en';
}

// Initial state
const initialState: PreferencesState = {
  language: getInitialLanguage(),
  notifications: defaultNotificationPreferences,
};

// Reducer
function preferencesReducer(state: PreferencesState, action: PreferencesAction): PreferencesState {
  switch (action.type) {
    case 'SET_LANGUAGE':
      return {
        ...state,
        language: action.payload,
      };

    case 'UPDATE_NOTIFICATIONS':
      return {
        ...state,
        notifications: {
          ...state.notifications,
          ...action.payload,
        },
      };

    case 'HYDRATE':
      return action.payload;

    default:
      return state;
  }
}

// Context value interface
interface PreferencesContextValue extends PreferencesState {
  setLanguage: (language: SupportedLanguage) => void;
  updateNotifications: (updates: Partial<NotificationPreferences>) => void;
}

// Create context
const PreferencesContext = createContext<PreferencesContextValue | undefined>(undefined);

// Storage helper functions
function savePreferencesToStorage(state: PreferencesState): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save preferences to localStorage:', error);
  }
}

function loadPreferencesFromStorage(): PreferencesState | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(PREFERENCES_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as PreferencesState;
      // Validate language
      if (!supportedLanguages.includes(parsed.language)) {
        return {
          ...parsed,
          language: 'en',
        };
      }
      return parsed;
    }
  } catch (error) {
    console.warn('Failed to load preferences from localStorage:', error);
  }
  return null;
}

// Provider component
interface PreferencesProviderProps {
  children: ReactNode;
}

export function PreferencesProvider({ children }: PreferencesProviderProps) {
  const [state, dispatch] = useReducer(preferencesReducer, initialState);

  // Hydrate from localStorage on mount
  useEffect(() => {
    const storedPreferences = loadPreferencesFromStorage();
    if (storedPreferences) {
      dispatch({ type: 'HYDRATE', payload: storedPreferences });
      // Sync i18n with stored language
      if (storedPreferences.language !== i18n.language) {
        void i18n.changeLanguage(storedPreferences.language);
      }
    }
  }, []);

  // Persist to localStorage when state changes
  useEffect(() => {
    savePreferencesToStorage(state);
  }, [state]);

  // Sync i18n when language changes
  useEffect(() => {
    if (i18n.language !== state.language) {
      void i18n.changeLanguage(state.language);
    }
  }, [state.language]);

  const setLanguage = useCallback((language: SupportedLanguage) => {
    dispatch({ type: 'SET_LANGUAGE', payload: language });
  }, []);

  const updateNotifications = useCallback((updates: Partial<NotificationPreferences>) => {
    dispatch({ type: 'UPDATE_NOTIFICATIONS', payload: updates });
  }, []);

  const value: PreferencesContextValue = {
    ...state,
    setLanguage,
    updateNotifications,
  };

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

// Custom hook
// eslint-disable-next-line react-refresh/only-export-components
export function usePreferences(): PreferencesContextValue {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
}

export default PreferencesContext;
